import { Fragment, ReactNode, useContext } from 'react';
import * as R from 'ramda';

import { Collapse, List } from '@atom/mui';

import SubItemGroupRow from './SubItemGroupRow';
import SubItemRow from './SubItemRow';
import SubItemsContext, { defaultRowPolicy } from './SubItemsContext';
import { hasDownstreamChanges } from './subItemTreeReducer';

type SubItemBranchProps = {
  parentId: string;
  depth: number;
};

const SubItemBranch = ({ parentId, depth }: SubItemBranchProps) => {
  const {
    tree,
    permissions,
    rowPolicy = defaultRowPolicy,
    selectedId,
    scrollToId,
    onScrolled,
    onRowClick,
    onToggleGroup,
    approveChanges,
    openModal,
  } = useContext(SubItemsContext);

  const contents = tree.contents[parentId];

  if (R.isNil(contents)) {
    return null;
  }

  const renderElement = (id: string, rowDepth: number): ReactNode => {
    const node = tree.nodes[id];

    return R.isNil(node) ? null : (
      <Fragment key={id}>
        <SubItemRow
          node={node}
          depth={rowDepth}
          isSelected={selectedId === id}
          isExpanded={Boolean(tree.expanded[id])}
          isLoading={tree.status[id] === 'loading'}
          hasDownstreamChanges={hasDownstreamChanges(tree, id)}
          shouldScroll={scrollToId === id}
          permissions={permissions}
          rowPolicy={rowPolicy}
          onClick={onRowClick}
          onApprove={approveChanges}
          onOpenModal={openModal}
          onScrolled={onScrolled}
        />
        {node.hasChildren && (
          <Collapse
            in={Boolean(tree.expanded[id])}
            timeout="auto"
            unmountOnExit
          >
            <List disablePadding>
              <SubItemBranch parentId={id} depth={rowDepth + 1} />
            </List>
          </Collapse>
        )}
      </Fragment>
    );
  };

  return (
    <>
      {contents.ungroupedIds.map(id => renderElement(id, depth))}
      {contents.groups.map(group => (
        <Fragment key={group.key}>
          <SubItemGroupRow
            group={group}
            depth={depth}
            isExpanded={Boolean(tree.expanded[group.key])}
            hasDownstreamChanges={group.elementIds.some(id =>
              Boolean(tree.nodes[id]?.changeType),
            )}
            onToggle={onToggleGroup}
          />
          <Collapse
            in={Boolean(tree.expanded[group.key])}
            timeout="auto"
            unmountOnExit
          >
            <List disablePadding>
              {group.elementIds.map(id => renderElement(id, depth + 1))}
            </List>
          </Collapse>
        </Fragment>
      ))}
    </>
  );
};

export default SubItemBranch;
